<template>
  <modal id="multicastGroupsForm"
         v-model="visible"
         :title="id ? $t('a-multicastGroupsForm:updateMulticastGroup') : $t('a-multicastGroupsForm:createMulticastGroup')"
         no-footer
         size="lg"
         @modal:cancel="cancel"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('a-multicastGroupsForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <stepper ref="stepper" v-model="step" :steps="steps" :on-next="next" />

      <template v-if="step === 0">
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="cluster" class="control-label">{{ $t('a-multicastGroupsForm:cluster') | capitalize }} *</label>
            <select id="cluster"
                    key="cluster"
                    v-model="cluster"
                    v-validate="'required'"
                    :data-vv-as="$t('a-multicastGroupsForm:cluster')"
                    :class="{ 'is-invalid': errors.has('cluster') }"
                    name="cluster"
                    class="form-control"
            >
              <template v-if="isRole.superAdmin">
                <optgroup v-for="(clusters, customer) in groupedClusters" :key="customer" :label="customer">
                  <option v-for="c in clusters" :key="c.id" :value="c" :data-cluster-id="c.id">{{ c.name }}</option>
                </optgroup>
              </template>

              <template v-else>
                <option v-for="c in $store.getters.allClusters" :key="c.id" :value="c" :data-cluster-id="c.id">{{ c.name }}</option>
              </template>
            </select>
            <div class="invalid-feedback">{{ errors.first('cluster') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="mcAddr" class="control-label">{{ $t('a-multicastGroupsForm:mcAddr') | capitalize }} *</label>
            <input id="mcAddr"
                   key="mcAddr"
                   v-model="mcAddr"
                   v-validate="'required|hexa8'"
                   :data-vv-as="$t('a-multicastGroupsForm:mcAddr')"
                   :class="{ 'is-invalid': errors.has('mcAddr') }"
                   type="text"
                   name="mcAddr"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('mcAddr') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="name" class="control-label">{{ $t('a-multicastGroupsForm:name') | capitalize }}</label>
            <input id="name"
                   key="name"
                   v-model="name"
                   v-validate="'text'"
                   :data-vv-as="$t('a-multicastGroupsForm:name')"
                   :class="{ 'is-invalid': errors.has('name') }"
                   type="text"
                   name="name"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('name') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="appSKey" class="control-label">{{ $t('a-multicastGroupsForm:appSKey') | capitalize }}<span v-if="!id"> *</span></label>
            <input id="appSKey"
                   key="appSKey"
                   v-model="appSKey"
                   v-validate="{ rules: { required: !id, hexa32: true } }"
                   :data-vv-as="$t('a-multicastGroupsForm:appSKey')"
                   :class="{ 'is-invalid': errors.has('appSKey') }"
                   :placeholder="id ? '********' : ''"
                   type="text"
                   name="appSKey"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('appSKey') }}</div>
          </div>

          <div class="form-group col-lg-6">
            <label for="nwkSKey" class="control-label">{{ $t('a-multicastGroupsForm:nwkSKey') | capitalize }}<span v-if="!id"> *</span></label>
            <input id="nwkSKey"
                   key="nwkSKey"
                   v-model="nwkSKey"
                   v-validate="{ rules: { required: !id, hexa32: true } }"
                   :data-vv-as="$t('a-multicastGroupsForm:nwkSKey')"
                   :class="{ 'is-invalid': errors.has('nwkSKey') }"
                   :placeholder="id ? '********' : ''"
                   type="text"
                   name="nwkSKey"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('nwkSKey') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-4'">
            <label for="rfRegion" class="control-label">{{ $t('a-multicastGroupsForm:rfRegion') | capitalize }} *</label>
            <select id="rfRegion"
                    key="rfRegion"
                    v-model="rfRegion"
                    v-validate="'required'"
                    :data-vv-as="$t('a-multicastGroupsForm:rfRegion')"
                    :class="{ 'is-invalid': errors.has('rfRegion') }"
                    name="rfRegion"
                    class="form-control"
            >
              <option v-for="region in regions" :key="region" :value="region">{{ region }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('rfRegion') }}</div>
          </div>

          <div v-show="countries.length > 0" class="form-group col-lg-3">
            <label for="country" class="control-label">{{ $t('a-multicastGroupsForm:country') | capitalize }}</label>
            <select id="country"
                    v-model="country"
                    name="country"
                    class="form-control"
            >
              <option v-for="c in countries" :key="c" :value="c">{{ $t('countries:' + c) }}</option>
            </select>
          </div>

          <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-4'">
            <label for="macVersion" class="control-label">{{ $t('a-multicastGroupsForm:macVersion') | capitalize }} *</label>
            <select id="macVersion"
                    key="macVersion"
                    v-model="macVersion"
                    v-validate="'required'"
                    :data-vv-as="$t('a-multicastGroupsForm:macVersion')"
                    :class="{ 'is-invalid': errors.has('macVersion') }"
                    :disabled="macVersions.length===0"
                    name="macVersion"
                    class="form-control"
            >
              <option v-for="mv in macVersions" :key="mv" :value="mv">{{ mv }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('macVersion') }}</div>
          </div>

          <div class="form-group" :class="countries.length > 0 ? 'col-lg-3' : 'col-lg-4'">
            <label for="regParamsRevision" class="control-label">{{ $t('a-multicastGroupsForm:regParamsRevision') | capitalize }}<span v-if="macVersion!=='1.0.1'"> *</span></label>
            <select id="regParamsRevision"
                    key="regParamsRevision"
                    v-model="regParamsRevision"
                    v-validate="{ rules: { required: macVersion!=='1.0.1' }}"
                    :data-vv-as="$t('a-multicastGroupsForm:regParamsRevision')"
                    :class="{ 'is-invalid': errors.has('regParamsRevision') }"
                    :disabled="regionalParameterRevisions.length===0"
                    name="regParamsRevision"
                    class="form-control"
            >
              <option v-for="regionalParameterRevision in regionalParameterRevisions" :key="regionalParameterRevision" :value="regionalParameterRevision">{{ regionalParameterRevision }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('regParamsRevision') }}</div>
            <small v-if="macVersion==='1.0.1'" class="form-text text-muted">{{ $t('a-multicastGroupsForm:noRevFor101') | capitalize }}</small>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-lg-4">
            <label for="fcntDown" class="control-label">{{ $t('a-multicastGroupsForm:fcntDown') | capitalize }} *</label>
            <input id="fcntDown"
                   key="fcntDown"
                   v-model="fcntDown"
                   v-validate="'required|integer|between:0,4294967295'"
                   :data-vv-as="$t('a-multicastGroupsForm:fcntDown')"
                   :class="{ 'is-invalid': errors.has('fcntDown') }"
                   type="text"
                   name="fcntDown"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('fcntDown') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="freq" class="control-label">{{ $t('a-multicastGroupsForm:freq') | capitalize }} *</label>
            <input id="freq"
                   key="freq"
                   v-model="freq"
                   v-validate="'required|number'"
                   :data-vv-as="$t('a-multicastGroupsForm:freq')"
                   :class="{ 'is-invalid': errors.has('freq') }"
                   type="text"
                   name="freq"
                   class="form-control"
            >
            <small class="form-text text-muted">{{ $t('a-multicastGroupsForm:freqUnit') | capitalize }}</small>
            <div class="invalid-feedback">{{ errors.first('freq') }}</div>
          </div>

          <div class="form-group col-lg-4">
            <label for="dataRate" class="control-label">{{ $t('a-multicastGroupsForm:dataRate') | capitalize }} *</label>
            <select id="dataRate"
                    key="dataRate"
                    v-model="dataRate"
                    v-validate="'required'"
                    :data-vv-as="$t('a-multicastGroupsForm:dataRate')"
                    :class="{ 'is-invalid': errors.has('dataRate') }"
                    :disabled="dataRates.length===0"
                    name="dataRate"
                    class="form-control"
            >
              <option v-for="dr in dataRates" :key="dr.value" :value="dr.value">{{ dr.label }}</option>
            </select>
            <div class="invalid-feedback">{{ errors.first('dataRate') }}</div>
          </div>
        </div>
      </template>

      <template v-if="step === 1">
        <h3 class="mt-3">{{ $t('a-multicastGroupsForm:endDevices') | capitalize }}</h3>
        <p class="text-muted font-italic">{{ $t('a-multicastGroupsForm:endDevicesHelper') | capitalize }}</p>

        <div class="row endDevices-lists">
          <div class="col-md-6">
            <h4 class="d-flex justify-content-between align-items-center">
              {{ $t('a-multicastGroupsForm:available') | capitalize }}
              <input v-model="filterAvailableEndDevices" :placeholder="$t('a-multicastGroupsForm:filter') | capitalize" type="text" class="form-control form-control-sm">
            </h4>

            <ul class="list-group">
              <li v-if="!cluster && filteredAvailableEndDevices.length===0" class="text-center text-muted font-italic mt-3">{{ $t('a-multicastGroupsForm:noClusters') | capitalize }}</li>
              <li v-else-if="cluster && filteredAvailableEndDevices.length===0" class="text-center text-muted font-italic mt-3">{{ $t('a-multicastGroupsForm:noEndDevices') | capitalize }}</li>
              <li v-for="(endDevice, index) in filteredAvailableEndDevices"
                  v-else
                  :key="'available'+index"
                  :title="$t('a-multicastGroupsForm:clickToAdd') | capitalize"
                  class="list-group-item"
                  @click="addSelected(endDevice)"
              >
                {{ endDevice.devEui }}
              </li>
            </ul>
          </div>

          <div class="col-md-6">
            <h4 class="d-flex justify-content-between align-items-center">
              {{ $t('a-multicastGroupsForm:selected') | capitalize }}
              <input v-model="filterSelectedEndDevices" :placeholder="$t('a-multicastGroupsForm:filter') | capitalize" type="text" class="form-control form-control-sm">
            </h4>

            <ul class="list-group">
              <li v-for="(endDevice, index) in filteredSelectedEndDevices"
                  :key="'selected'+index"
                  :title="$t('a-multicastGroupsForm:clickToRemove') | capitalize"
                  class="list-group-item"
                  @click="removeSelected(endDevice)"
              >
                {{ endDevice.devEui }}
              </li>
            </ul>
          </div>
        </div>
      </template>
    </div>

    <div slot="modal-footer" class="w-100 text-right">
      <button v-if="step > 0" id="stepperPrevious" class="btn btn-flat float-left" @click="$refs.stepper.previous()">{{ $t('stepper:previous') | capitalize }}</button>
      <button id="stepperCancel" class="btn btn-flat" @click="cancel">{{ $t('stepper:cancel') | capitalize }}</button>
      <button v-if="step < steps.length-1" id="stepperNext" class="btn btn-flat" @click="$refs.stepper.next()">{{ $t('stepper:next') | capitalize }}</button>
      <button v-if="step === steps.length-1" id="stepperValidate" class="btn btn-flat" @click="id ? updateMulticastGroup() : createMulticastGroup()">{{ $t('stepper:validate') | capitalize }}</button>
    </div>
  </modal>
</template>

<script>
import { groupBy, orderBy, filter, isEmpty, debounce, each, pick, map, find } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import { countries } from '@/configuration/regions'
import regionMixin from '@/mixins/regionsMixin'

export default {
  mixins: [regionMixin],

  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    multicastGroupToEdit: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Form
      id: '',
      name: '',
      cluster: null,
      mcAddr: '',
      appSKey: '',
      nwkSKey: '',
      rfRegion: '',
      country: '',
      macVersion: '',
      regParamsRevision: '',
      fcntDown: 0,
      freq: '',
      dataRate: '',

      filterAvailableEndDevices: '',
      availableEndDevices: [],
      filterSelectedEndDevices: '',
      selectedEndDevices: [],
      // Stepper
      step: 0,
      steps: [
        { label: this.$t('a-multicastGroupsForm:identity') },
        { label: this.$t('a-multicastGroupsForm:endDevices') }
      ]
    }
  },

  computed: {
    countries () { return countries[this.rfRegion] || [] },

    // Group clusters by customer for superAdmin
    groupedClusters () {
      // Order by customer name
      // Group by customer name to add customer in lists
      return groupBy(orderBy(this.$store.getters.allClusters, ['customer.name']), 'customer.name')
    },

    // Filter available end-devices
    filteredAvailableEndDevices () {
      return orderBy(filter(this.availableEndDevices, endDevice => endDevice.devEui.toLowerCase().indexOf(this.filterAvailableEndDevices.toLowerCase()) > -1), ['devEui'])
    },

    // Filter selected end-devices
    filteredSelectedEndDevices () {
      return orderBy(filter(this.selectedEndDevices, endDevice => endDevice.devEui.toLowerCase().indexOf(this.filterSelectedEndDevices.toLowerCase()) > -1), ['devEui'])
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show) {
        this.$store.dispatch('getAllClusters').then(() => {
          if (this.id) this.cluster = find(this.$store.getters.allClusters, cluster => cluster.id === this.multicastGroupToEdit.cluster.id)
        })
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    // Reset end-devices list on cluster / region / macVersion / regParamsRevision changes
    cluster () {
      this.resetEndDevicesList()
    },
    rfRegion () {
      this.resetEndDevicesList()
      if (this.countries.length === 0) this.country = ''
    },
    macVersion () {
      this.resetEndDevicesList()
    },
    regParamsRevision () {
      this.resetEndDevicesList()
    },

    async multicastGroupToEdit () {
      if (!isEmpty(this.multicastGroupToEdit)) {
        this.id = this.multicastGroupToEdit.id
        this.name = this.multicastGroupToEdit.name
        this.mcAddr = this.multicastGroupToEdit.mcAddr
        this.fcntDown = this.multicastGroupToEdit.fcntDown
        this.freq = this.multicastGroupToEdit.freq
        this.rfRegion = this.multicastGroupToEdit.rfRegion
        this.country = this.multicastGroupToEdit.country
        // Keys are not in DTO when getting multicast group
        // So we display only ******** as placeholder and send them if they have changed
        this.appSKey = ''
        this.nwkSKey = ''

        // Using $nextTick to let the UI update rfRegion and then update macVersion and dataRate
        await this.$nextTick()
        this.macVersion = this.multicastGroupToEdit.macVersion
        this.dataRate = this.multicastGroupToEdit.dataRate

        // Using $nextTick to let the UI update macVersion and then update regParamsRevision
        await this.$nextTick()
        this.regParamsRevision = this.multicastGroupToEdit.regParamsRevision
      }
    }
  },

  methods: {
    async reset () {
      this.id = ''
      this.name = ''
      this.cluster = null
      this.mcAddr = ''
      this.appSKey = ''
      this.nwkSKey = ''
      // rfRegion, macVersion and regParamsRevision are declared in regionMixin
      this.rfRegion = ''
      this.country = ''
      this.macVersion = ''
      this.regParamsRevision = ''
      this.fcntDown = 0
      this.freq = ''
      this.dataRate = ''
      this.filterAvailableEndDevices = ''
      this.availableEndDevices = []
      this.selectedEndDevices = []
      this.step = 0

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    resetEndDevicesList: debounce(async function () {
      this.availableEndDevices = []
      this.selectedEndDevices = []

      // Get end-devices only when these fields are set:
      // Cluster
      // Region
      // LoRaWan MAC version
      // Regional parameters revision (only if LoRaWan MAC version !== 1.0.1)
      if (this.cluster && this.rfRegion && this.macVersion === '1.0.1') this.getEndDevices()
      if (this.cluster && this.rfRegion && this.macVersion !== '1.0.1' && this.regParamsRevision) this.getEndDevices()
    }, 500),

    async getEndDevices () {
      let search = []

      // Add criteria
      search = this.$utils.addSearch(search, 'eq', this.cluster.id, 'cluster.id')
      search = this.$utils.addSearch(search, 'eq', this.rfRegion, 'rfRegion')
      search = this.$utils.addSearch(search, 'eq', this.country, 'country')
      search = this.$utils.addSearch(search, 'eq', this.macVersion, 'macVersion')
      if (this.regParamsRevision) search = this.$utils.addSearch(search, 'eq', this.regParamsRevision, 'regParamsRevision')

      // Format criteria
      const options = { search: JSON.stringify(this.$utils.formatSearch('and', search)) }
      const params = this.$utils.getParams({ sort: '+devEui' }, options)

      let error, response
      [error, response] = await to(this.$api.getAllEndDevices(this.$store.getters.application, params))
      if (error || !response) {
        this.availableEndDevices = []
        return
      }

      this.availableEndDevices = response.data ? response.data.list : []

      // Edit: create the selected list and filter available list
      if (this.multicastGroupToEdit) {
        each(this.multicastGroupToEdit.endDevices, edDevEui => {
          const selectedEndDevice = find(this.availableEndDevices, aed => aed.devEui === edDevEui)
          if (selectedEndDevice) this.addSelected(selectedEndDevice)
        })
      }
    },

    addSelected (endDevice) {
      this.availableEndDevices = filter(this.availableEndDevices, ed => ed.devEui.toLowerCase() !== endDevice.devEui.toLowerCase())
      this.selectedEndDevices.push(endDevice)
    },

    removeSelected (endDevice) {
      this.availableEndDevices.push(endDevice)
      this.selectedEndDevices = filter(this.selectedEndDevices, ed => ed.devEui.toLowerCase() !== endDevice.devEui.toLowerCase())
    },

    cancel () {
      this.visible = false
      this.step = 0
      this.$emit('cancel')
    },

    async next () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      return true
    },

    async createMulticastGroup () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const fields = [
        'name', 'mcAddr',
        'appSKey', 'nwkSKey',
        'rfRegion', 'macVersion', 'regParamsRevision',
        'fcntDown', 'freq', 'dataRate'
      ]

      if (this.country) fields.push('country')
      const multicastGroup = pick(this, fields)
      multicastGroup.cluster = { id: this.cluster.id }
      multicastGroup.endDevices = map(this.selectedEndDevices, ed => ed.devEui)

      let error
      [error] = await to(this.$api.createMulticastGroup(this.$store.getters.application, multicastGroup))
      if (error) return

      this.visible = false
      this.step = 0
      // Success
      eventBus.$emit('message-success', this.$t('a-multicastGroupsForm:createSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    },

    async updateMulticastGroup () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const fields = [
        'name', 'mcAddr',
        'rfRegion', 'macVersion', 'regParamsRevision',
        'fcntDown', 'freq', 'dataRate'
      ]

      if (this.country) fields.push('country')
      const multicastGroup = pick(this, fields)
      multicastGroup.cluster = { id: this.cluster.id }
      multicastGroup.endDevices = map(this.selectedEndDevices, ed => ed.devEui)
      if (this.appSKey) multicastGroup.appSKey = this.appSKey
      if (this.nwkSKey) multicastGroup.nwkSKey = this.nwkSKey

      let error
      [error] = await to(this.$api.updateMulticastGroup(this.multicastGroupToEdit, multicastGroup))
      if (error) return

      this.visible = false
      this.step = 0
      // Success
      eventBus.$emit('message-success', this.$t('a-multicastGroupsForm:editSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .endDevices-lists {
    h4 {
      height: 48px;
      padding: 0.6rem 1rem;
      margin-bottom: 0;
      background: $gray-300;
      border-top-left-radius: $border-radius;
      border-top-right-radius: $border-radius;

      input {
        width: 100px;
        transition: all ease 0.35s;

        &:focus {
          width: 200px;
        }
      }
    }

    ul.list-group {
      height: 350px;
      max-height: 350px;
      padding: 0;
      overflow-y: auto;
      list-style: none;
      border: 1px solid $gray-300;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: $border-radius;
      border-bottom-left-radius: $border-radius;

      li.list-group-item {
        padding: 0.6rem 1rem;
        cursor: pointer;
        user-select: none;
        border-top: 1px solid $gray-300;
        border-bottom: 1px solid $gray-300;
        border-radius: 0;
      }
    }
  }
</style>
